import { createEcommerceEvent } from "../../tracking/create-ecommerce-event";
import { createEvent } from "../../tracking/create-event";

// REV-97: diagnostyczny wrapper łapiący nieudane wywołania zdarzeń (telemetria błędów trackingu).
export const eventMissingEvent = createEvent<{
  expectedEventName: string;
  errorContext: string;
}>("event_missing");

export function safeTrackEvent(eventFn: () => void, expectedEventName: string, errorContext: string) {
  try {
    eventFn();
  } catch (error) {
    eventMissingEvent({ expectedEventName, errorContext: `${errorContext}: ${String(error)}` });
  }
}

// Rozpoczęcie realizacji kodu vouchera (wejście w przepływ kodu).
export const voucherCodeStartEvent = createEvent("voucher_code_start");
// REV-95: błędny / odrzucony kod vouchera.
export const voucherCodeErrorEvent = createEvent<{ errorType: string }>("voucher_code_error");

// REV-84: wyświetlenie listy produktów (katalog/listing); price/discount/currency/index ogarnia createEcommerceEvent.
export const viewItemListEvent = createEcommerceEvent("view_item_list");

// REV-18: migrated from a manual pushGtmEvent in select-distribution.tsx into the shared catalog.
// Not an ecommerce event (no items[]); uses the plain createEvent factory like the other B2B events.
export const viewOfferListEvent = createEvent<{ partnerName: string; availableOffers: string }>("view_offer_list");

// REV-86: wyświetlenie szczegółów pojedynczego produktu.
export const viewItemEvent = createEcommerceEvent<{ value: number }>("view_item", {}, { includeIsUpsell: true });

// REV-85: wyświetlenie wyników wyszukiwania (z liczbą wyszukań i pierwszymi wynikami).
export const viewSearchResultsEvent = createEvent<{
  searchTerm: string;
  findResults: boolean;
  numberOfSearches: number;
  firstResult?: string;
  secondResult?: string;
  thirdResult?: string;
}>("view_search_results");

// Cart events (REV-86: Added value parameter)
// REV-16: removed add_to_cart_limit_exceeded and view_cart — defined but never called. They suggested
// data that doesn't exist, so they were dropped rather than wired up (audit decision).
export const addToCartEvent = createEcommerceEvent<{ value: number }>("add_to_cart", {}, { includeIsUpsell: true });
// Usunięcie produktu z koszyka (usunięcie pozycji upsell).
export const removeFromCartEvent = createEcommerceEvent<{ value: number }>(
  "remove_from_cart",
  {},
  { includeIsUpsell: true },
);

// REV-88: przejście na stronę upsell.
export const goToUpsellEvent = createEvent("go_to_upsell");

// REV-87: rozpoczęcie checkoutu (z numberOfProducts, bez indeksu w items).
export const beginCheckoutEvent = createEcommerceEvent<{ value: number }, { numberOfProducts: number }>(
  "begin_checkout",
  {},
  { includeIndex: false },
);

// User type captured at registration/verification time and replayed on payment + purchase events.
// Mirrors group-actions-ordering's `UserType` so funnel reporting can join the two modules on the
// same dimension.
export type UserType = "new_registered" | "existing" | "guest";

// REV-89: odsłona strony podsumowania/płatności (ta sama struktura co begin_checkout).
export const viewPaymentInfoEvent = createEcommerceEvent<
  { value: number },
  { numberOfProducts: number; userType: UserType | null }
>("view_payment_info", {}, { includeIndex: false });

// REV-95: błąd finalizacji checkoutu (errorType w danych root).
export const finishCheckoutErrorEvent = createEcommerceEvent<
  { value: number },
  { errorType: string; numberOfProducts: number }
>("finish_checkout_error", {}, { includeIndex: false });

// REV-90: klik opłacenia zamówienia (przemianowane z finish_checkout; dodano paymentType i orderFor).
export const addPaymentInfoEvent = createEcommerceEvent<
  { value: number; transactionId: string },
  { paymentType: string; orderFor?: string; numberOfProducts: number; userType: UserType | null }
>("add_payment_info", {}, { includeIndex: false });

// REV-96: zakup zakończony (ta sama struktura co add_payment_info).
export const purchaseEvent = createEcommerceEvent<
  { value: number; transactionId: string },
  { paymentType: string; orderFor?: string; numberOfProducts: number; userType: UserType | null }
>("purchase", {}, { includeIndex: false });

// REV-91: zapis do newslettera.
export const signUpNewsletterEvent = createEvent("sign_up_newsletter");

// Wybór vouchera promocyjnego (typ promocji).
export const selectPromoVoucherEvent = createEvent<{
  promotionType: string;
}>("select_promo_voucher");

// Wybór adresu dostawy produktów na etapie zakupu.
export const purchaseProductsAddressSelectionEvent = createEvent<{
  selectedCity: string;
  selectedAddress: string;
}>("purchase_products_address_selection");

// Zdarzenia weryfikacji + rejestracji. Słownik odzwierciedla moduł GAO, by analityka obu modułów mówiła
// tym samym językiem. Obie ścieżki nowego użytkownika (pełna rejestracja z hasłem oraz uproszczona
// rejestracja gościa bez hasła) korzystają z tej samej nakładki OTP, więc rozróżnia je to, co użytkownik
// wypełnił — przekazywane do analityki przez `method`:
//   - "account"  — logowanie OTP istniejącego użytkownika (krok HasAccountOTP)
//   - "sign_up"  — nowy użytkownik, pełna rejestracja (wpisane hasło)
//   - "guest"    — nowy użytkownik, uproszczona/szkicowa rejestracja (zakup gościa, bez hasła)
// user_id pozostaje null dla gości i dla restricted_app_access; w innym wypadku to uuid pacjenta zwrócony
// przez backend.
export const verificationViewEvent = createEvent<{ method: "account" | "guest" | "sign_up" }>("verification_view");

// Kod weryfikacyjny zaakceptowany, użytkownik przechodzi dalej.
export const verificationSuccessEvent = createEvent<{
  userId: string | null;
  method: "account" | "guest" | "sign_up";
}>("verification_success");

// Kod weryfikacyjny odrzucony.
export const verificationErrorEvent = createEvent<{
  errorType: string;
  method: "account" | "guest" | "sign_up";
}>("verification_error");

// Użytkownik kliknął „wyślij kod ponownie".
export const verificationResendCodeEvent = createEvent<{ method: "account" | "guest" | "sign_up" }>(
  "verification_resend_code",
);

// Ścieżka rejestracji dla nowych kont wybierających pełną rejestrację (z hasłem). Uproszczone rejestracje
// gościa nie odpalają sign_up — zamiast tego odpalają verification_success z method:"guest".
export const signUpViewEvent = createEvent("sign_up_view");

// Rejestracja zakończona sukcesem (user_id = uuid pacjenta albo null dla restricted_app_access).
export const signUpEvent = createEvent<{ userId: string | null }>("sign_up");

// Rejestracja nieudana.
export const signUpErrorEvent = createEvent<{ errorType: string }>("sign_up_error");
